/**
 * useAssinaturaActions - Ações de ciclo de vida da Assinatura (Recorrência)
 *
 * Centraliza as 4 mutations server-side (Fase 3 das RPCs):
 *   - anteciparCiclo    → emitir_ciclo_assinatura + financial-charge (provider)
 *   - atualizar         → update_financeiro_assinatura (só ciclos futuros)
 *   - pausar/despausar  → pause_financeiro_assinatura
 *   - cancelar          → cancel_financeiro_assinatura (opcionalmente cancela
 *                          ciclos já emitidos no provider via provider-proxy)
 *
 * Todas as chamadas invalidam o cache da listagem e do detalhe.
 *
 * @version 1.6.0
 * RELEASE: 2026-06-27 v1.6.0 - Pro-rata edição: quando update_financeiro_assinatura
 *                              (v1.10.0) retorna bloco `dispatch` em
 *                              mid_cycle_proration/pro_rata_result, a mutation
 *                              `atualizar` roteia automaticamente a cobrança
 *                              recém-criada ao provider — espelhando o que
 *                              acontece no 1º ciclo da recorrência (PIX/boleto
 *                              via dispatchCobrancaToProvider) e o botão
 *                              Antecipar com cartão (financial-charge-enqueue +
 *                              auto_charge_after_create). Fire-and-forget — não
 *                              bloqueia o fechamento do modal.
 * RELEASE: 2026-06-09 v1.0.0 - Criação inicial (Fase 4 do módulo Recorrência)
 * RELEASE: 2026-06-16 v1.1.0 - Sync com provider migrado para PGMQ via
 *                              financial-charge-enqueue.
 * RELEASE: 2026-06-16 v1.2.0 - RPC update_financeiro_assinatura v1.7.0 vira
 *                              fonte única de enfileiramento.
 * RELEASE: 2026-06-21 v1.3.0 - Fase 2 hardening: cancelamento de ciclos
 *                              emitidos passa pelo financial-charge v2.0.0
 *                              (DELETE correto + verificação outbound).
 *                              Corrige bug silencioso: provider-proxy não
 *                              implementava cancel_charge — ciclos jamais
 *                              eram cancelados no Asaas.
 * RELEASE: 2026-06-21 v1.4.0 - Fase 1 hardening recorrência cartão:
 *                              anteciparCiclo agora roteia para
 *                              financial-charge-enqueue + auto_charge_after_create
 *                              quando a assinatura tem default_card_token_id.
 *                              Mesma rota do cron diário (emitir_ciclos_due
 *                              v1.2.0) — idempotency_key compartilhada garante
 *                              que concorrência cron×botão converge em replay.
 *                              Assinaturas sem cartão padrão mantêm o fluxo
 *                              anterior (resend_to_provider → PIX/boleto).
 * RELEASE: 2026-06-21 v1.5.0 - Feature 2 anti-fraude: anteciparCiclo agora
 *                              aceita `auditPayload` opcional e grava evento
 *                              `subscription_anticipated` em
 *                              financeiro_assinatura_eventos via RPC
 *                              `log_subscription_anticipate_event` ANTES de
 *                              disparar o débito. Garante trilha de auditoria
 *                              mesmo se a chamada ao provider falhar depois.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { dispatchCobrancaToProvider } from '@/lib/financial/dispatchCobranca';

const logger = createLogger('AssinaturaActions');

export interface UpdateAssinaturaPayload {
  descricao?: string;
  observacoes?: string;
  valor_total?: number;
  meio_pagamento_id?: string;
  conta_corrente_id?: string;
  dias_antes_emissao?: number;
  // v1.2.0: modo de emissão da cobrança
  tipo_emissao?: 'dias_antes' | 'dia_fixo';
  dia_emissao?: number | null;
  proximo_vencimento?: string;          // v1.1.0: novo (somente datas futuras)
  ciclos_total?: number | null;         // v1.1.0: novo (reduzir cancela excedentes)
  /** v1.4.0: dias para expirar a cobrança após o vencimento (1..60). */
  dias_expiracao_pos_vencimento?: number;
  incluir_itens_descricao?: boolean;
  desconto?: Record<string, unknown>;
  multa?: Record<string, unknown>;
  juros?: Record<string, unknown>;
  reajuste?: Record<string, unknown>;
  tags?: string[];
  metadata?: Record<string, unknown>;
  itens?: Array<{
    product_id: string;
    descricao: string;
    quantidade: number;
    valor_unitario: number;
    desconto: number;
    /** v1.2.1: tipo de desconto persistido em metadata para hidratar o toggle %/R$. */
    desconto_tipo?: 'percent' | 'fixed';
    /** v1.2.1: percentual original (0–100) quando desconto_tipo='percent'. */
    desconto_percent?: number;
  }>;
  /** Optimistic locking — ISO do updated_at corrente carregado no modal */
  updated_at_etag?: string;
  /** Idempotency key — UUID gerado no client */
  idempotency_key?: string;
  /**
   * v1.3.0 — Bloco pro-rata.
   * `consume_customer_credit`: flag enviada quando a política
   * `customer_credit_prompt_mode='ask'` e o usuário marcou o checkbox no
   * preview mid-cycle. Para política 'auto' a flag é IGNORADA pelo backend
   * (consome sempre). Para política 'disabled' o backend também IGNORA.
   * Anti-tampering: o backend é a única fonte de verdade.
   */
  pro_rata?: {
    consume_customer_credit?: boolean;
  };
}

/** Cobrança que precisa de sync com o provider após edição. */
export interface SyncProviderRequired {
  cobranca_id: string;
  provider_charge_id: string | null;
  provider_slug: string | null;
  status: string;
  ciclo_numero: number;
}

export function useAssinaturaActions(assinaturaId?: string | null) {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  /** Invalida caches da listagem e do detalhe corrente. */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['financeiro-assinaturas-paginated'] });
    queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas-paginated'] });
    if (assinaturaId) {
      queryClient.invalidateQueries({ queryKey: ['assinatura-detalhes', assinaturaId] });
    }
  };

  // ===========================================================================
  // 1) Antecipar emissão de um ciclo específico
  //    aguardando_emissao → pendente → dispara financial-charge no provider
  //
  // v1.5.0 (Feature 2): a mutation aceita tanto `string` (cobranca_id) quanto
  // um objeto `{ cobrancaId, auditPayload }`. Quando o auditPayload está
  // presente, a RPC `log_subscription_anticipate_event` é chamada ANTES de
  // qualquer disparo ao provider, garantindo trilha mesmo em caso de falha.
  // ===========================================================================
  const anteciparCiclo = useMutation({
    mutationFn: async (
      arg:
        | string
        | {
            cobrancaId: string;
            auditPayload?: {
              valor: number;
              dias_antecipados: number;
              step_up_method: string; // ex.: 'typed_keyword:ANTECIPAR'
              idempotency_key?: string;
            };
          },
    ) => {
      const cobrancaId = typeof arg === 'string' ? arg : arg.cobrancaId;
      const auditPayload = typeof arg === 'string' ? undefined : arg.auditPayload;

      // ---------------------------------------------------------------------
      // PASSO 0 — PROMOÇÃO DO CICLO (emitir_ciclo_assinatura)
      // ---------------------------------------------------------------------
      const { data, error } = await (supabase.rpc as any)('emitir_ciclo_assinatura', {
        p_cobranca_id: cobrancaId,
        p_tenant_hint: tenantId,
      });
      if (error) throw new Error(error.message);

      const payload = data as {
        cobranca_id: string;
        assinatura_id: string;
        ciclo_numero: number;
        tipo_pagamento: string;
        valor_total: number;
        data_vencimento: string;
        descricao: string;
        numero_parcelas?: number;
        tipo_cobranca?: string;
        provider_slug?: string | null;
        // v1.1.0 da RPC — só presentes quando assinatura tem cartão default
        default_card_token_id?: string | null;
        conta_corrente_id?: string | null;
        customer_id?: string | null;
      };

      // ---------------------------------------------------------------------
      // PASSO 1 — AUDITORIA OBRIGATÓRIA (Feature 2)
      // Grava o evento `subscription_anticipated` ANTES de qualquer débito.
      // Falha aqui aborta o fluxo: melhor não cobrar do que cobrar sem trilha.
      // O User Agent é coletado client-side; o IP é resolvido pelo gateway.
      // ---------------------------------------------------------------------
      if (auditPayload) {
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;
        const idemKey =
          auditPayload.idempotency_key ||
          `antecip:${payload.assinatura_id}:c${payload.ciclo_numero}:${Date.now()}`;
        try {
          const { error: auditError } = await (supabase.rpc as any)(
            'log_subscription_anticipate_event',
            {
              p_assinatura_id:    payload.assinatura_id,
              p_cobranca_id:      payload.cobranca_id,
              p_ciclo_numero:     payload.ciclo_numero,
              p_valor:            auditPayload.valor,
              p_dias_antecipados: auditPayload.dias_antecipados,
              p_step_up_method:   auditPayload.step_up_method,
              p_idempotency_key:  idemKey,
              p_ip_address:       null, // resolvido server-side por trigger/gateway, se aplicável
              p_user_agent:       ua,
              p_tenant_hint:      tenantId,
            },
          );
          if (auditError) {
            logger.error('Falha ao registrar auditoria de antecipação', { error: auditError.message });
            throw new Error(
              `Auditoria obrigatória falhou — cobrança NÃO foi disparada: ${auditError.message}`,
            );
          }
        } catch (auditErr: any) {
          // Re-lança para abortar a mutation antes do débito
          throw auditErr instanceof Error ? auditErr : new Error(String(auditErr));
        }
      }


      // =====================================================================
      // v1.4.0 — ROTEAMENTO POR CARTÃO PADRÃO
      // INVARIANTE RECORRÊNCIA-CARTÃO: quando há default_card_token_id, NÃO
      // chamamos charge_with_token diretamente (que exige pay_xxx). Em vez
      // disso, enfileiramos em q_charge com auto_charge_after_create=true —
      // o worker cria pay_xxx via financial-charge e só depois dispara o
      // débito no cartão tokenizado, garantindo que nenhum caminho recorrente
      // gere cobrança duplicada no Asaas. Mesma rota do cron diário.
      // =====================================================================
      if (payload.default_card_token_id && payload.conta_corrente_id) {
        const idemKey = `assin:${payload.assinatura_id}:ciclo:${payload.ciclo_numero}:cob:${payload.cobranca_id}`;

        try {
          const { error: enqueueError } = await supabase.functions.invoke('financial-charge-enqueue', {
            body: {
              action: 'create_single',
              tenant_id: tenantId,
              cobranca_id: payload.cobranca_id,
              conta_corrente_id: payload.conta_corrente_id,
              crm_customer_id: payload.customer_id,
              valor_total: payload.valor_total,
              data_vencimento: payload.data_vencimento,
              descricao: payload.descricao,
              tipo_pagamento: 'credit_card',
              numero_parcelas: payload.numero_parcelas ?? 1,
              observacoes: null,
              // Cartão recorrente — pass-through v1.2.0 do enqueue
              card_token_id: payload.default_card_token_id,
              card_idempotency_key: idemKey,
              auto_charge_after_create: true,
            },
          });
          if (enqueueError) {
            logger.error('Falha ao enfileirar cobrança com cartão default', { error: enqueueError.message });
            toast.warning(
              `Ciclo promovido, mas o enfileiramento da cobrança falhou: ${enqueueError.message}. Será retentado pelo cron.`,
            );
          }
        } catch (err) {
          logger.error('Erro de comunicação ao enfileirar cobrança recorrente', { error: String(err) });
          toast.warning(
            `Ciclo promovido, mas o envio da cobrança falhou: ${String(err)}. Será retentado pelo cron.`,
          );
        }

        return data;
      }

      // ---------------------------------------------------------------------
      // Caminho original (sem cartão default) — PIX/boleto via resend_to_provider
      // ---------------------------------------------------------------------
      try {
        const { error: providerError } = await supabase.functions.invoke('financial-charge', {
          body: {
            action: 'resend_to_provider',
            cobranca_id: payload.cobranca_id,
          },
        });
        if (providerError) {
          logger.error('Provider retornou erro ao antecipar', { error: providerError.message });
          toast.warning(`Ciclo promovido, mas envio ao provider falhou: ${providerError.message}. Será retentado.`);
        }
      } catch (err) {
        logger.error('Falha de comunicação com provider', { error: String(err) });
        toast.warning(`Ciclo promovido, mas sincronização com provider falhou: ${String(err)}.`);
      }

      return data;
    },
    onSuccess: () => {
      toast.success('Emissão antecipada com sucesso!');
      invalidate();
    },
    onError: (e: Error) => {
      logger.error('Erro ao antecipar ciclo', { error: e.message });
      toast.error(`Erro ao antecipar: ${e.message}`);
    },
  });

  // ===========================================================================
  // 2) Editar assinatura (propaga somente para ciclos futuros)
  // ===========================================================================
  const atualizar = useMutation({
    mutationFn: async (payload: UpdateAssinaturaPayload) => {
      if (!assinaturaId) throw new Error('assinaturaId obrigatório');
      // ETAPA BLOQUEANTE: apenas a RPC. O sync com provider externo NÃO bloqueia
      // o fechamento do modal — roda em segundo plano (fire-and-forget) para
      // evitar travar o botão "Salvar alterações" quando o provider está lento
      // ou retorna upstream timeout. Falhas são apenas avisadas via toast.
      const { data, error } = await (supabase.rpc as any)('update_financeiro_assinatura', {
        p_assinatura_id: assinaturaId,
        p_payload: payload as unknown as Record<string, unknown>,
        p_tenant_hint: tenantId,
      });
      if (error) throw new Error(error.message);

      // =====================================================================
      // v1.6.0 — DESPACHO AUTOMÁTICO DA COBRANÇA PRO-RATA
      // A RPC v1.10.0 retorna `dispatch` em mid_cycle_proration (e/ou em
      // pro_rata_result) quando uma cobrança pro-rata nova nasce em
      // 'aguardando_emissao'. Aqui replicamos o mesmo roteamento usado em
      // anteciparCiclo e useCreateSubscription:
      //   - Cartão recorrente (default_card_token_id presente)
      //       → financial-charge-enqueue + auto_charge_after_create=true
      //         (worker cria pay_xxx e debita o cartão tokenizado).
      //   - Demais meios (PIX/boleto/etc.)
      //       → dispatchCobrancaToProvider (create_single via financial-charge).
      // Fire-and-forget: falhas geram apenas toast.warning — o worker
      // continua tendo retry e o botão "Reenviar ao provider" segue como
      // fallback manual.
      // =====================================================================
      const dispatchPayload =
        (data?.mid_cycle_proration?.dispatch as
          | {
              cobranca_id: string;
              conta_corrente_id: string;
              crm_customer_id: string;
              valor_total: number;
              data_vencimento: string;
              descricao: string;
              tipo_pagamento: string;
              numero_parcelas?: number;
              default_card_token_id?: string | null;
            }
          | undefined) ||
        (data?.pro_rata_result?.dispatch as
          | {
              cobranca_id: string;
              conta_corrente_id: string;
              crm_customer_id: string;
              valor_total: number;
              data_vencimento: string;
              descricao: string;
              tipo_pagamento: string;
              numero_parcelas?: number;
              default_card_token_id?: string | null;
            }
          | undefined);

      if (dispatchPayload?.cobranca_id) {
        // Idempotency key alinhada ao padrão de anteciparCiclo —
        // assin:{assinatura_id}:ciclo:0:cob:{cobranca_id} (ciclo 0 = pro-rata).
        const idemKey = `assin:${assinaturaId}:ciclo:0:cob:${dispatchPayload.cobranca_id}`;

        if (dispatchPayload.default_card_token_id && dispatchPayload.conta_corrente_id) {
          // Caminho cartão recorrente — mesmo do botão Antecipar com cartão.
          try {
            const { error: enqueueError } = await supabase.functions.invoke(
              'financial-charge-enqueue',
              {
                body: {
                  action: 'create_single',
                  tenant_id: tenantId,
                  cobranca_id: dispatchPayload.cobranca_id,
                  conta_corrente_id: dispatchPayload.conta_corrente_id,
                  crm_customer_id: dispatchPayload.crm_customer_id,
                  valor_total: dispatchPayload.valor_total,
                  data_vencimento: dispatchPayload.data_vencimento,
                  descricao: dispatchPayload.descricao,
                  tipo_pagamento: 'credit_card',
                  numero_parcelas: dispatchPayload.numero_parcelas ?? 1,
                  observacoes: null,
                  card_token_id: dispatchPayload.default_card_token_id,
                  card_idempotency_key: idemKey,
                  auto_charge_after_create: true,
                },
              },
            );
            if (enqueueError) {
              logger.warn('Falha ao enfileirar pro-rata no cartão default', {
                cobranca_id: dispatchPayload.cobranca_id,
                error: enqueueError.message,
              });
              toast.warning(
                'Pro-rata gerado, envio ao provider será retentado automaticamente.',
              );
            }
          } catch (err) {
            logger.warn('Erro de comunicação ao enfileirar pro-rata recorrente', {
              cobranca_id: dispatchPayload.cobranca_id,
              error: String(err),
            });
            toast.warning(
              'Pro-rata gerado, envio ao provider será retentado automaticamente.',
            );
          }
        } else {
          // Caminho PIX/boleto/etc. — mesmo do 1º ciclo (useCreateSubscription).
          const ok = await dispatchCobrancaToProvider({
            cobranca_id: dispatchPayload.cobranca_id,
            conta_corrente_id: dispatchPayload.conta_corrente_id,
            crm_customer_id: dispatchPayload.crm_customer_id,
            tipo_pagamento: dispatchPayload.tipo_pagamento,
            observacoes: null,
            origin: 'subscription_mid_cycle_pro_rata',
          });
          if (!ok) {
            toast.warning(
              'Pro-rata gerado, envio ao provider será retentado automaticamente.',
            );
          }
        }
      }

      return data;
    },
    onSuccess: (data: any) => {
      const cancExc = data?.ciclos_cancelados_excesso || 0;
      const ciclos = data?.ciclos_atualizados || 0;
      let msg = 'Recorrência atualizada!';
      if (ciclos > 0) msg += ` ${ciclos} ciclos futuros atualizados.`;
      if (cancExc > 0) msg += ` ${cancExc} ciclos excedentes cancelados.`;
      toast.success(msg);
      invalidate();

      // ===== Sync com provider (v1.2.0) =====
      // A RPC update_financeiro_assinatura v1.7.0 já enfileira cada cobrança
      // afetada em q_charge dentro da própria transação (fonte única de
      // verdade). Aqui apenas informamos o usuário — NÃO chamamos
      // financial-charge-enqueue para evitar duplicidade.
      const syncList = (data?.sync_provider_required || []) as SyncProviderRequired[];
      if (Array.isArray(syncList) && syncList.length > 0) {
        const targets = syncList.filter((s) => !!s.provider_charge_id);
        if (targets.length > 0) {
          logger.info('Sync com provider enfileirado pela RPC em q_charge', {
            total: targets.length,
          });
          toast.info(
            `${targets.length} cobrança(s) enviada(s) para sincronização com o provider.`,
          );
        }
      }

      // ===== Mid-cycle proration aplicada (Fase C) =====
      // A RPC v1.9.0 retorna `mid_cycle_proration` com o resultado do bloco
      // automático por item. Quando aplicado, mostramos toast informativo
      // com o destino (cobrança extra, próximo ciclo ou crédito).
      const mcp = data?.mid_cycle_proration as
        | {
            applied?: boolean;
            destino?: string;
            charge_id?: string | null;
            target_id?: string | null;
            credit_id?: string | null;
            delta_total?: number;
            factor?: number;
          }
        | undefined;
      if (mcp?.applied) {
        const abs = Math.abs(Number(mcp.delta_total || 0));
        const valorFmt = abs.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        if (mcp.destino === 'charge_immediately') {
          // v1.6.0: texto adaptativo conforme o meio despachado pela RPC.
          const hasCardToken = !!data?.mid_cycle_proration?.dispatch?.default_card_token_id;
          const description = hasCardToken
            ? 'Ajuste pro-rata cobrado no cartão cadastrado.'
            : 'Ajuste pro-rata enviado ao provider — aguardando pagamento.';
          toast.success(`Ajuste pro-rata: cobrança extra de ${valorFmt} gerada.`, {
            description,
            action: {
              label: 'Ver cobranças',
              onClick: () => window.location.assign('/tenant/financial/charges'),
            },
          });
        } else if (
          mcp.destino === 'add_to_next_invoice' ||
          mcp.destino === 'add_to_next_invoice_no_target'
        ) {
          toast.success(`Ajuste pro-rata: ${valorFmt} incorporado ao próximo ciclo.`, {
            action: {
              label: 'Ver cobranças',
              onClick: () => window.location.assign('/tenant/financial/charges'),
            },
          });
        } else if (mcp.destino === 'credit') {
          toast.success(`Ajuste pro-rata: crédito de ${valorFmt} gerado para o cliente.`);
        }
      }
    },


    onError: (e: Error) => {
      logger.error('Erro ao atualizar assinatura', { error: e.message });
      // Em conflito de ETag, força refetch do detalhe para próximo Save pegar updated_at novo
      const isConflict = /40001|alterada por outro/i.test(e.message);
      if (isConflict && assinaturaId) {
        queryClient.invalidateQueries({ queryKey: ['assinatura-detalhes', assinaturaId] });
        toast.error('Recorrência foi alterada em paralelo. Recarregamos os dados — tente novamente.');
        return;
      }
      toast.error(`Erro ao atualizar: ${e.message}`);
    },
  });

  // ===========================================================================
  // 3) Pausar / Despausar
  // ===========================================================================
  const pausar = useMutation({
    mutationFn: async (pausar: boolean) => {
      if (!assinaturaId) throw new Error('assinaturaId obrigatório');
      const { data, error } = await (supabase.rpc as any)('pause_financeiro_assinatura', {
        p_assinatura_id: assinaturaId,
        p_pausar: pausar,
        p_tenant_hint: tenantId,
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (data: any) => {
      toast.success(data?.status === 'pausada' ? 'Recorrência pausada' : 'Recorrência ativada');
      invalidate();
    },
    onError: (e: Error) => {
      logger.error('Erro ao alternar pausa', { error: e.message });
      toast.error(`Erro: ${e.message}`);
    },
  });

  // ===========================================================================
  // 4) Cancelar (com opção de cancelar também os ciclos emitidos no provider)
  //
  // v1.3.0 (Fase 2 do hardening 2026-06-21):
  //   - Trocado provider-proxy (que NÃO implementava cancel_charge — bug
  //     silencioso, ciclos nunca eram cancelados no Asaas) por
  //     financial-charge action='cancel_charge'.
  //   - Enviamos apenas cobranca_id; o handler v2.0.0 resolve
  //     provider_charge_id/conta_corrente_id do banco (anti-tampering),
  //     roteia DELETE correto (avulsa, parcelada, parcela) e faz
  //     verificação outbound (GET pós-DELETE) antes de marcar cancelado.
  // ===========================================================================
  const cancelar = useMutation({
    mutationFn: async (cancelarPendentesNoProvider: boolean) => {
      if (!assinaturaId) throw new Error('assinaturaId obrigatório');
      const { data, error } = await (supabase.rpc as any)('cancel_financeiro_assinatura', {
        p_assinatura_id: assinaturaId,
        p_cancelar_pendentes_no_provider: cancelarPendentesNoProvider,
        p_tenant_hint: tenantId,
      });
      if (error) throw new Error(error.message);

      // Cancela cada ciclo já emitido no provider via handler com hardening.
      const pendentes = (data?.pendentes_provider || []) as Array<{
        cobranca_id: string;
        provider_charge_id: string | null;
        provider_slug: string | null;
      }>;

      if (cancelarPendentesNoProvider && pendentes.length > 0) {
        let falhas = 0;
        for (const p of pendentes) {
          if (!p.provider_charge_id) continue;
          try {
            const { data: result, error: invokeError } = await supabase.functions.invoke(
              'financial-charge',
              {
                body: {
                  action: 'cancel_charge',
                  // Apenas cobranca_id: handler resolve o resto do banco
                  cobranca_id: p.cobranca_id,
                },
              },
            );
            if (invokeError || (result && result.success === false)) {
              falhas++;
              logger.warn('Falha ao cancelar ciclo no provider', {
                cobrancaId: p.cobranca_id,
                error: invokeError?.message || result?.error,
              });
            }
          } catch (err) {
            falhas++;
            logger.warn('Erro de comunicação ao cancelar ciclo no provider', {
              cobrancaId: p.cobranca_id,
              error: String(err),
            });
          }
        }
        if (falhas > 0) {
          toast.warning(`${falhas} ciclo(s) não puderam ser cancelados no provider — verifique a tela de cobranças.`);
        }
      }

      return data;
    },
    onSuccess: (data: any) => {
      toast.success(`Recorrência cancelada (${data?.ciclos_cancelados ?? 0} ciclos futuros)`);
      invalidate();
    },
    onError: (e: Error) => {
      logger.error('Erro ao cancelar assinatura', { error: e.message });
      toast.error(`Erro ao cancelar: ${e.message}`);
    },
  });

  return {
    anteciparCiclo,
    atualizar,
    pausar,
    cancelar,
    isBusy:
      anteciparCiclo.isPending ||
      atualizar.isPending ||
      pausar.isPending ||
      cancelar.isPending,
  };
}
